import React, { useEffect, useState } from 'react';
import { learningAPI } from '../services/api';

const Leaderboard = () => {
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUserRank, setCurrentUserRank] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        const fetchLeaders = async () => {
            try {
                const res = await learningAPI.getLeaderboard(20);
                // Handle both formats: direct array and wrapped response
                const leaderboardData = res.data?.leaderboard || res.data || [];
                setLeaders(leaderboardData);
                setLastUpdated(new Date());
                
                // Robust current user detection
                const localStorageUser = JSON.parse(localStorage.getItem('user') || '{}');
                const myId = localStorageUser.user_id || localStorageUser.id;
                
                if (myId && leaderboardData.length > 0) {
                    const userRank = leaderboardData.find(u => (u.user_id === myId || u.id === myId));
                    if (userRank) {
                        setCurrentUserRank(userRank);
                    } else {
                        setCurrentUserRank(null);
                    }
                } else {
                    setCurrentUserRank(null);
                }
            } catch (err) {
                console.error("Leaderboard error", err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchLeaders();
        
        // Auto-refresh leaderboard every 30 seconds
        const interval = setInterval(fetchLeaders, 30000);
        return () => clearInterval(interval);
    }, []);

    const getBadge = (rank) => {
        if (rank === 1) return "👑";
        if (rank === 2) return "🥈";
        if (rank === 3) return "🥉";
        return null;
    };

    return (
        <div style={styles.card}>
            <div style={styles.header}>
                <h3 style={styles.title}>🏆 Top Learners</h3>
                <span style={styles.icon}>📊</span>
            </div>

            <div style={styles.list}>
                {leaders.map((u, idx) => {
                    const myId = currentUserRank?.user_id || currentUserRank?.id;
                    const thisId = u.user_id || u.id;
                    const isMe = myId && thisId && myId === thisId;

                    return (
                        <div
                            key={u.user_id || idx}
                            style={{
                                ...styles.row,
                                background: u.rank <= 3 ? 'linear-gradient(90deg, rgba(39, 176, 91, 0.05) 0%, rgba(255,255,255,0) 100%)' : 'transparent',
                                borderLeft: u.rank <= 3 ? '3px solid #27b05b' : '3px solid transparent',
                                ...(isMe ? styles.currentUserRow : {})
                            }}
                        >
                            <div style={{ ...styles.rank, color: u.rank <= 3 ? '#27b05b' : '#adb5bd' }}>
                                #{u.rank}
                            </div>
                            <div style={styles.name}>
                                <div style={styles.avatar}>
                                    {u.name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                                        {isMe ? 'You' : (u.name || u.email?.split('@')[0] || 'Learner')}
                                        {isMe && <span style={{ fontSize: '11px', color: '#27b05b', marginLeft: '6px' }}>● Online</span>}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Level {u.level || 1}</span>
                                </div>
                                {getBadge(u.rank) && <span style={styles.badge}>{getBadge(u.rank)}</span>}
                            </div>
                            <div style={{ ...styles.score, color: 'var(--primary)' }}>
                                🪙 {(u.zen_coins || 0).toLocaleString()} 
                            </div>
                        </div>
                    );
                })}
                {leaders.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        {loading ? '⏳ Loading stats...' : '📚 Complete lessons to join the leaderboard!'}
                    </div>
                )}
            </div>

            <div style={styles.footer}>
                <span>Earn 50 ZenCoins per lesson to climb the ranks!</span>
                {lastUpdated && (
                    <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>
                        Updated just now
                    </span>
                )}
            </div>
        </div>
    );
};

const styles = {
    card: {
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        borderRadius: '24px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    title: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1f2937',
        margin: 0,
    },
    icon: {
        fontSize: '20px',
        background: '#ecfdf5',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#27b05b'
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderRadius: '12px',
        transition: 'transform 0.2s',
        marginBottom: '4px'
    },
    currentUserRow: {
        background: 'linear-gradient(90deg, rgba(79, 70, 229, 0.1) 0%, rgba(255,255,255,0) 100%)',
        borderLeft: '3px solid #4f46e5',
    },
    rank: {
        width: '35px',
        fontWeight: '800',
        fontSize: '16px',
    },
    name: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#374151',
        fontSize: '14px'
    },
    avatar: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '700',
        boxShadow: '0 2px 5px rgba(79, 70, 229, 0.2)'
    },
    badge: {
        fontSize: '16px'
    },
    score: {
        fontWeight: '700',
        color: '#1f2937',
        fontSize: '15px'
    },
    footer: {
        marginTop: '20px',
        fontSize: '12px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: '500',
        background: '#f9fafb',
        padding: '8px 12px',
        borderRadius: '8px'
    },
};

export { Leaderboard };
export default Leaderboard;
